import { SEO } from "../components/common/SEO";
import { PortraitPlaceholder } from "../components/common/BrandVisuals";
import { useLanguage } from "../context/LanguageProvider";
import { useSiteData } from "../context/SiteDataProvider";
import { pick, UI_STRINGS } from "../lib/i18n";
import { Award, ScrollText } from "lucide-react";

export default function About() {
    const { lang } = useLanguage();
    const { siteContent } = useSiteData();
    const about = siteContent?.about || {};
    const seo = siteContent?.seo?.about || {};

    const bioText = pick(about.bio, lang) || "";
    const paragraphs = bioText.split(/\n\s*\n/).filter(Boolean);

    return (
        <div data-testid="page-about" className="bg-ink">
            <SEO
                title={pick(seo.title, lang) || "À propos"}
                description={pick(seo.description, lang)}
                lang={lang}
            />
            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <PortraitPlaceholder
                        src={siteContent?.images?.portrait_url || ""}
                        className="aspect-[3/4] w-full sticky top-28 object-cover"
                        alt="Maître Mina Adja Bintou Sanou"
                    />
                </div>
                <div className="lg:col-span-7 rise-in">
                    <div className="eyebrow mb-3">
                        {pick(UI_STRINGS.nav.about, lang)}
                    </div>
                    <h1 className="font-serif text-4xl md:text-5xl leading-tight text-neutral-50">
                        {pick(about.title, lang)}
                    </h1>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <span className="text-xs tracking-widest uppercase text-gold border border-gold px-3 py-1.5">
                            Barreau du Québec · 2020
                        </span>
                        <span className="text-xs tracking-widest uppercase text-gold border border-gold px-3 py-1.5">
                            {pick(
                                {
                                    fr: "Barreau du Burkina Faso · 2022",
                                    en: "Bar of Burkina Faso · 2022",
                                },
                                lang,
                            )}
                        </span>
                    </div>
                    <div className="prose-mabs mt-10">
                        {paragraphs.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values */}
            <section
                className="bg-ink-deep border-y border-ink-border py-20 md:py-24"
                data-testid="about-values"
            >
                <div className="mx-auto max-w-7xl px-5 lg:px-10">
                    <div className="max-w-2xl mb-12">
                        <div className="eyebrow mb-3">
                            {pick(UI_STRINGS.sections.values, lang)}
                        </div>
                        <h2 className="font-serif text-3xl md:text-4xl text-neutral-50 leading-tight">
                            {pick(
                                {
                                    fr: "Une pratique fondée sur quatre exigences",
                                    en: "A practice built on four demands",
                                },
                                lang,
                            )}
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {(about.values || []).map((v, i) => (
                            <div
                                key={i}
                                className="card-line p-8"
                                data-testid={`about-value-${i}`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 flex items-center justify-center border border-gold/60 bg-ink shrink-0">
                                        <Award
                                            size={18}
                                            className="text-gold"
                                            strokeWidth={1.5}
                                        />
                                    </div>
                                    <div>
                                        <h3 className="font-serif text-xl text-neutral-50 mb-2">
                                            {pick(v.title, lang)}
                                        </h3>
                                        <p className="text-sm text-neutral-300 leading-relaxed">
                                            {pick(v.text, lang)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {about.oath_note && (
                        <div
                            className="mt-10 flex items-start gap-4 text-sm text-neutral-300 max-w-3xl"
                            data-testid="about-oath"
                        >
                            <ScrollText
                                size={20}
                                className="text-gold mt-0.5 shrink-0"
                            />
                            <p className="italic leading-relaxed">
                                {pick(about.oath_note, lang)}
                            </p>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
